// JeopardyGame — team-based category board game that reuses lecture quiz questions.
// Three roles:
//   • host  — picks cells, sees correct answers, judges each team's response
//   • player — assigned to Red or Blue team; buzzes in, sees question/options
//   • spectator — read-only (parents / co-teachers) via /game/watch/CODE
//
// Flow:
//   1. Host opens game → joins the board → students scan /join/CODE and get auto-assigned to teams
//   2. Host taps a cell → question reveals to the whole room
//   3. Players tap "BUZZ" — first team to buzz is "on the clock"
//   4. Host taps "Correct" or "Wrong" on the active team
//   5. Correct → that team banks the points; cell closes.
//      Wrong  → that team loses the points; other team can buzz if they haven't tried
//   6. Board clears → final team scores + podium
import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Zap, Crown, Users, Loader2, Home, Check, X, Flag, Volume2, VolumeX, ChevronLeft,
} from "lucide-react";
import { Avatar } from "@/components/Avatar";
import {
  getGame, getGameByCode, watchGame,
  jeopardyPick, jeopardyBuzz, jeopardyJudge, endGame,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const VALUES = [100, 200, 300, 400, 500];
const POLL_MS = 1100;

// --- small sfx helper -----------------------------------------------
const useSfx = () => {
  const [muted, setMuted] = useState(() => localStorage.getItem("lectura_sfx_muted") === "1");
  const beep = (freq = 880, dur = 0.08, type = "triangle", vol = 0.15) => {
    if (muted) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = type; o.frequency.value = freq;
      g.gain.value = vol;
      o.connect(g); g.connect(ctx.destination);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      o.stop(ctx.currentTime + dur);
    } catch {}
  };
  return {
    muted, toggle: () => { setMuted((m) => { localStorage.setItem("lectura_sfx_muted", !m ? "1" : "0"); return !m; }); },
    pick:    () => beep(660, 0.14, "square", 0.1),
    buzz:    () => { beep(220, 0.3, "sawtooth", 0.14); setTimeout(() => beep(180, 0.3, "sawtooth", 0.14), 100); },
    right:   () => { beep(880, 0.1); setTimeout(() => beep(1320, 0.14), 100); },
    wrong:   () => beep(140, 0.4, "sawtooth", 0.14),
    end:     () => { beep(660, 0.1); setTimeout(() => beep(880, 0.1), 120); setTimeout(() => beep(1200, 0.3), 240); },
  };
};

export default function JeopardyGame({ mode, initialGame = null, codeOverride = null }) {
  const params = useParams();
  const { user } = useAuth();
  const sfx = useSfx();

  const [game, setGame] = useState(initialGame);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const lastBuzzRef = useRef(null);

  // Resolve the game code (search param, URL param, or passed-in override).
  const resolvedCode = (codeOverride || params.code || initialGame?.code || "").toUpperCase();

  // --- Poll game state ---
  useEffect(() => {
    let active = true;
    let timer = null;
    const fetchOnce = async () => {
      try {
        let g;
        if (mode === "host") g = await getGame(params.id || initialGame?.id);
        else if (mode === "watch") g = await watchGame(resolvedCode);
        else g = await getGameByCode(resolvedCode);
        if (!active) return;
        // SFX cues when state changes
        if (game && g?.jeopardy?.buzz_team && g.jeopardy.buzz_team !== lastBuzzRef.current) {
          sfx.buzz();
          lastBuzzRef.current = g.jeopardy.buzz_team;
        }
        if (!g?.jeopardy?.buzz_team) lastBuzzRef.current = null;
        setGame(g);
      } catch (e) {
        if (e?.response?.status === 404 && !game) setNotFound(true);
      }
      if (active) timer = setTimeout(fetchOnce, POLL_MS);
    };
    fetchOnce();
    return () => { active = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, params.id, resolvedCode]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-blue-950 to-slate-900 text-white grid place-items-center px-6">
        <div className="text-center max-w-md">
          <Flag className="h-12 w-12 text-amber-400 mx-auto" />
          <h1 className="mt-4 font-serif text-3xl font-black">Game not found</h1>
          <p className="mt-2 text-white/70">Double-check the room code with your teacher.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-5 py-2 text-sm font-semibold hover:bg-white/20 transition">
            <Home className="h-4 w-4" /> Home
          </Link>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-blue-950 to-slate-900 text-white grid place-items-center">
        <Loader2 className="h-10 w-10 animate-spin text-amber-300" />
      </div>
    );
  }

  const j = game.jeopardy || {};
  const isHost = mode === "host";
  const isSpectator = mode === "watch";
  const myTeam = j.my_team;

  // --- host actions ---
  const pick = async (key) => {
    if (!isHost || busy) return;
    setBusy(true);
    try {
      const g = await jeopardyPick(game.id, key);
      sfx.pick();
      setGame(g);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open cell");
    } finally { setBusy(false); }
  };

  const judge = async (team, correct) => {
    if (!isHost || busy) return;
    setBusy(true);
    try {
      const g = await jeopardyJudge(game.id, team, correct);
      correct ? sfx.right() : sfx.wrong();
      setGame(g);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't judge");
    } finally { setBusy(false); }
  };

  const buzz = async () => {
    if (isHost || isSpectator || !myTeam || j.buzz_team || !j.active_cell || busy) return;
    setBusy(true);
    try {
      const g = await jeopardyBuzz(game.id);
      setGame(g);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buzz failed");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    if (!isHost) return;
    if (!window.confirm("End the game now?")) return;
    try {
      await endGame(game.id);
      sfx.end();
      toast.success("Game ended");
      setGame((g) => ({ ...g, status: "final" }));
    } catch (e) {
      toast.error("Couldn't end game");
    }
  };

  const bgCls = "min-h-screen bg-gradient-to-br from-indigo-950 via-blue-950 to-slate-900 text-white";

  return (
    <div className={bgCls}>
      <div className="max-w-6xl mx-auto px-4 py-5 sm:px-6 sm:py-7">
        <TopBar game={game} isHost={isHost} isSpectator={isSpectator} muted={sfx.muted} onToggleSound={sfx.toggle} />

        {game.status === "waiting" && (
          <Lobby game={game} isHost={isHost} onPick={pick} />
        )}

        {(game.status === "running" || game.status === "reveal") && (
          <>
            <Scoreboard j={j} myTeam={myTeam} />
            <Board j={j} isHost={isHost} isSpectator={isSpectator} onPick={pick} />
            {j.active_cell && (
              <ActiveCell
                cell={j.board[j.active_cell]}
                j={j}
                isHost={isHost}
                isSpectator={isSpectator}
                myTeam={myTeam}
                onBuzz={buzz}
                onJudge={judge}
              />
            )}
            {isHost && (
              <div className="mt-6 flex justify-end">
                <button onClick={finish}
                        className="inline-flex items-center gap-2 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-200 px-4 py-2 text-xs font-bold hover:bg-rose-500/30 transition">
                  <Flag className="h-3.5 w-3.5" /> End game early
                </button>
              </div>
            )}
          </>
        )}

        {game.status === "final" && (
          <FinalPodium j={j} />
        )}
      </div>
    </div>
  );
}

// --- Top bar -----------------------------------------------
const TopBar = ({ game, isHost, isSpectator, muted, onToggleSound }) => (
  <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
    <div className="flex items-center gap-3">
      <Link to="/" className="rounded-full bg-white/10 border border-white/20 p-2 hover:bg-white/20 transition" aria-label="Home">
        <ChevronLeft className="h-4 w-4" />
      </Link>
      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-300/40 text-amber-200 px-3 py-0.5 text-[10px] font-bold uppercase tracking-[0.3em]">
          <Trophy className="h-3 w-3" /> Jeopardy{isSpectator && " · spectator"}
        </div>
        <h1 className="mt-1 font-serif text-xl sm:text-2xl font-black leading-tight">{game.lecture_title}</h1>
        <p className="text-xs text-white/50">Hosted by {game.host_name} · {game.players?.length || 0} student{(game.players?.length || 0) === 1 ? "" : "s"}</p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button onClick={onToggleSound}
              aria-label={muted ? "Unmute" : "Mute"}
              className="rounded-full bg-white/10 border border-white/20 p-2 hover:bg-white/20 transition">
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
      <div className="text-right">
        <p className="text-[10px] uppercase tracking-widest text-white/40">Room</p>
        <p data-testid="jeopardy-code" className="font-mono text-2xl font-black tracking-widest">{game.code}</p>
      </div>
    </div>
  </header>
);

// --- Lobby --------------------------------------------------
const Lobby = ({ game, isHost, onPick }) => {
  const players = game.players || [];
  const red = players.filter((p) => (game.jeopardy?.team_red?.members || []).includes?.(p.id) || p._team === "red");
  const blue = players.filter((p) => !red.includes(p));
  return (
    <section className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 sm:p-10 text-center">
      <h2 className="font-serif text-3xl sm:text-4xl font-black">
        Join at <span className="font-mono text-amber-300">lectura.app/join/{game.code}</span>
      </h2>
      <p className="mt-2 text-white/60">Students will be auto-assigned to Red or Blue teams as they join.</p>

      <div className="mt-8 grid grid-cols-2 gap-4">
        {["red", "blue"].map((color) => {
          const list = players.filter((_, i) => (i % 2 === 0 ? "red" : "blue") === color);
          const bg = color === "red" ? "bg-rose-500/15 border-rose-400/40" : "bg-sky-500/15 border-sky-400/40";
          const text = color === "red" ? "text-rose-200" : "text-sky-200";
          return (
            <div key={color} className={`rounded-2xl border ${bg} p-5 text-left min-h-[180px]`}>
              <h3 className={`font-black uppercase tracking-widest text-xs ${text}`}>Team {color}</h3>
              <p className="mt-2 text-white/70 text-sm">{list.length} player{list.length === 1 ? "" : "s"}</p>
              <ul className="mt-3 space-y-1.5">
                {list.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 text-sm">
                    <Avatar id={p.avatar} size={20} />
                    {p.name}
                    {p.is_host_player && <Crown className="h-3 w-3 text-amber-300" />}
                  </li>
                ))}
                {list.length === 0 && <li className="text-white/30 italic text-xs">Waiting…</li>}
              </ul>
            </div>
          );
        })}
      </div>

      {isHost && (
        <div className="mt-8">
          <p className="text-xs text-white/50 mb-3">Game starts when you pick the first cell.</p>
          <p className="text-amber-300 font-bold text-sm">↓ Scroll to the board below ↓</p>
        </div>
      )}

      {isHost && players.length > 0 && (
        <div className="mt-8">
          <Scoreboard j={game.jeopardy} myTeam={null} />
        </div>
      )}
      {isHost && (
        <div className="mt-6">
          <p className="text-xs text-white/50 mb-2 text-center">Preview the board — tap any cell to start the game</p>
          <Board j={game.jeopardy} isHost={true} isSpectator={false} onPick={onPick} />
        </div>
      )}
    </section>
  );
};

// --- Team scoreboard ---------------------------------------
const Scoreboard = ({ j, myTeam }) => {
  if (!j) return null;
  const red = j.team_red || { score: 0, member_count: 0 };
  const blue = j.team_blue || { score: 0, member_count: 0 };
  return (
    <div data-testid="scoreboard" className="grid grid-cols-2 gap-3 sm:gap-4 mb-5">
      <TeamCard color="red"  team={red}  active={j.buzz_team === "red"}  highlight={myTeam === "red"} />
      <TeamCard color="blue" team={blue} active={j.buzz_team === "blue"} highlight={myTeam === "blue"} />
    </div>
  );
};

const TeamCard = ({ color, team, active, highlight }) => {
  const base = color === "red"
    ? { border: "border-rose-400/40", bg: "bg-rose-500/15", text: "text-rose-200", acc: "bg-rose-500" }
    : { border: "border-sky-400/40",  bg: "bg-sky-500/15",  text: "text-sky-200",  acc: "bg-sky-500" };
  return (
    <motion.div
      animate={active ? { scale: [1, 1.03, 1] } : {}}
      transition={active ? { duration: 0.6, repeat: Infinity } : {}}
      className={`relative rounded-2xl border ${base.border} ${base.bg} px-4 py-3 sm:px-5 sm:py-4 ${highlight ? "ring-2 ring-amber-300" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`uppercase tracking-widest text-[10px] sm:text-xs font-black ${base.text}`}>Team {color}</p>
          <p className="text-xs text-white/60">{team.member_count} player{team.member_count === 1 ? "" : "s"}</p>
        </div>
        {active && (
          <span className={`${base.acc} text-white rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider`}>
            <Zap className="h-3 w-3 inline" /> ON THE CLOCK
          </span>
        )}
      </div>
      <p className={`mt-1 font-mono text-3xl sm:text-5xl font-black tabular-nums ${team.score < 0 ? "text-rose-400" : "text-white"}`}>
        {team.score.toLocaleString()}
      </p>
    </motion.div>
  );
};

// --- The 5x5 board ------------------------------------------
const Board = ({ j, isHost, isSpectator, onPick }) => {
  if (!j?.categories?.length) return null;
  const categories = j.categories;
  return (
    <section data-testid="jeopardy-board" className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-3 sm:p-4 mt-2">
      <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
        {/* category row */}
        {categories.map((c, i) => (
          <div key={i} className="rounded-lg bg-amber-500/90 text-slate-900 px-2 py-3 text-center font-black uppercase tracking-wider text-[10px] sm:text-xs leading-tight min-h-[56px] grid place-items-center">
            {c}
          </div>
        ))}
        {/* value rows */}
        {VALUES.map((v) => (
          categories.map((_, ci) => {
            const key = `${ci}:${v}`;
            const cell = j.board?.[key];
            if (!cell) {
              return <div key={key} className="rounded-lg bg-white/5 opacity-40 min-h-[56px]" />;
            }
            const done = cell.answered_by_team;
            const active = key === j.active_cell;
            const canPick = isHost && !done && !active && !j.active_cell;
            const label = done ? "✓" : `$${v}`;
            const colorCls = done === "red"
              ? "bg-rose-500/40 text-rose-100"
              : done === "blue"
                ? "bg-sky-500/40 text-sky-100"
                : done === "none"
                  ? "bg-slate-600/40 text-slate-400 line-through"
                  : active
                    ? "bg-amber-400 text-slate-900 ring-2 ring-amber-200"
                    : "bg-indigo-700 hover:bg-indigo-600 text-amber-300";
            return (
              <button
                key={key}
                data-testid={`cell-${key}`}
                onClick={() => canPick && onPick(key)}
                disabled={!canPick}
                className={`rounded-lg transition font-mono text-base sm:text-xl font-black px-2 py-3 min-h-[56px] ${colorCls} ${canPick ? "cursor-pointer" : "cursor-default"}`}
                title={done ? `Won by Team ${done === "none" ? "nobody" : done}` : active ? "Currently open" : `$${v} — ${categories[ci]}`}
              >
                {label}
              </button>
            );
          })
        ))}
      </div>
      {isSpectator && (
        <p className="mt-3 text-xs text-white/40 text-center">Spectator view — host picks cells, teams buzz in to answer.</p>
      )}
    </section>
  );
};

// --- Active-cell question + buzz / judge --------------------
const ActiveCell = ({ cell, j, isHost, isSpectator, myTeam, onBuzz, onJudge }) => {
  if (!cell || !cell.question) return null;
  const canBuzz = !isHost && !isSpectator && myTeam && !j.buzz_team;
  return (
    <AnimatePresence>
      <motion.section
        key={j.active_cell}
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        className="mt-5 rounded-3xl border-2 border-amber-400/60 bg-amber-500/10 backdrop-blur p-5 sm:p-7"
        data-testid="active-cell"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-300">
              {j.categories[cell.cat_idx]} · ${cell.value}
            </p>
            <h3 className="mt-2 font-serif text-lg sm:text-2xl font-black leading-snug">{cell.question}</h3>
          </div>
        </div>
        {Array.isArray(cell.options) && cell.options.length > 0 && (
          <ol className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {cell.options.map((opt, i) => {
              const isCorrect = isHost && cell.correct_index === i;
              return (
                <li key={i} className={`rounded-xl border px-3 py-2.5 text-sm ${isCorrect ? "border-emerald-400 bg-emerald-500/15 text-emerald-100 font-bold" : "border-white/15 bg-white/5 text-white/85"}`}>
                  <span className="opacity-50 font-mono mr-2">{String.fromCharCode(65 + i)}</span>
                  {opt}
                  {isCorrect && <span className="ml-2 text-emerald-300 text-[10px] font-black uppercase tracking-wider">correct</span>}
                </li>
              );
            })}
          </ol>
        )}

        {/* Buzz / judge controls */}
        <div className="mt-6">
          {isHost ? (
            j.buzz_team ? (
              <div className="rounded-2xl bg-white/5 p-4">
                <p className="text-sm text-white/70">
                  <span className={`font-black uppercase ${j.buzz_team === "red" ? "text-rose-300" : "text-sky-300"}`}>Team {j.buzz_team}</span> buzzed in. Did they answer correctly?
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid="judge-correct"
                          onClick={() => onJudge(j.buzz_team, true)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white transition">
                    <Check className="h-4 w-4" /> Correct · +${cell.value}
                  </button>
                  <button data-testid="judge-wrong"
                          onClick={() => onJudge(j.buzz_team, false)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 hover:bg-rose-600 px-5 py-2.5 text-sm font-bold text-white transition">
                    <X className="h-4 w-4" /> Wrong · -${cell.value}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-white/60 italic">Waiting for a team to buzz in…</p>
            )
          ) : isSpectator ? (
            j.buzz_team ? (
              <p className="text-sm text-white/70">
                <span className={`font-bold ${j.buzz_team === "red" ? "text-rose-300" : "text-sky-300"}`}>Team {j.buzz_team}</span> is answering…
              </p>
            ) : (
              <p className="text-sm text-white/50 italic">Waiting for a buzz…</p>
            )
          ) : (
            // Player: show BUZZ button or team-waiting state
            canBuzz ? (
              <button data-testid="buzz-btn"
                      onClick={onBuzz}
                      className={`w-full rounded-2xl py-5 text-lg font-black uppercase tracking-widest shadow-lg transition
                        ${myTeam === "red" ? "bg-rose-500 hover:bg-rose-600 text-white" : "bg-sky-500 hover:bg-sky-600 text-white"}`}>
                <Zap className="h-5 w-5 inline mr-2" /> BUZZ! Team {myTeam}
              </button>
            ) : j.buzz_team === myTeam ? (
              <div className="rounded-2xl bg-emerald-500/20 border border-emerald-400/40 p-4 text-center">
                <p className="font-black uppercase tracking-widest text-emerald-200">You're up — answer out loud!</p>
                <p className="mt-1 text-xs text-emerald-300/70">Your teacher will mark it correct or wrong.</p>
              </div>
            ) : j.buzz_team ? (
              <div className="rounded-2xl bg-white/5 border border-white/15 p-4 text-center">
                <p className="text-sm text-white/60">
                  <span className={`font-bold ${j.buzz_team === "red" ? "text-rose-300" : "text-sky-300"}`}>Team {j.buzz_team}</span> is answering…
                </p>
              </div>
            ) : !myTeam ? (
              <p className="text-center text-sm text-white/50 italic">You're not on a team yet — rejoin the room.</p>
            ) : null
          )}
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

// --- Final podium -----------------------------------------
const FinalPodium = ({ j }) => {
  const r = j.team_red?.score || 0;
  const b = j.team_blue?.score || 0;
  const winner = r === b ? "tie" : (r > b ? "red" : "blue");
  return (
    <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-8 sm:p-12 text-center mt-6">
      <Trophy className="h-16 w-16 text-amber-300 mx-auto" />
      {winner === "tie" ? (
        <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-black">It's a tie!</h2>
      ) : (
        <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-black">
          <span className={winner === "red" ? "text-rose-300" : "text-sky-300"}>Team {winner.toUpperCase()}</span> wins!
        </h2>
      )}
      <div className="mt-8 grid grid-cols-2 gap-4 max-w-md mx-auto">
        <div className="rounded-2xl border border-rose-400/40 bg-rose-500/15 p-5">
          <p className="uppercase text-rose-200 font-black tracking-widest text-xs">Red</p>
          <p className="mt-1 font-mono text-4xl font-black">${r.toLocaleString()}</p>
        </div>
        <div className="rounded-2xl border border-sky-400/40 bg-sky-500/15 p-5">
          <p className="uppercase text-sky-200 font-black tracking-widest text-xs">Blue</p>
          <p className="mt-1 font-mono text-4xl font-black">${b.toLocaleString()}</p>
        </div>
      </div>
      <Link to="/" className="mt-10 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 px-6 py-3 text-sm font-bold transition">
        <Home className="h-4 w-4" /> Back to lectures
      </Link>
    </div>
  );
};
